import Button from "../Button/Button";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { twMerge } from "tailwind-merge";
import { useId } from "../../../hooks/useId";
import Checkbox from "../Checkbox";
import Divider from "../Divider/Divider";

export default function SelectMulti({
  buttonProps = {},
  className,
  header,
  onChange,
  onClear,
  options,
  placeholder,
  values,
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Keep selected visible
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const selectedSet = useMemo(() => new Set(values), [values]);

  const filtered = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    const base = q
      ? options.filter(
          (o) =>
            o.text.toLowerCase().includes(q) ||
            o.value.toLowerCase().includes(q)
        )
      : options;
    // selected first, then unselected
    const selected = base.filter((o) => selectedSet.has(o.value));
    const unselected = base.filter((o) => !selectedSet.has(o.value));
    return { selected, unselected };
  }, [options, searchValue, selectedSet]);

  const displayText = useMemo(() => {
    if (!values.length) return placeholder;
    const first = options.find((o) => o.value === values[0])?.text ?? values[0];
    return values.length > 1 ? `${first} (+${values.length - 1})` : first;
  }, [values, options, placeholder]);

  const handleToggle = (val) => {
    onChange(val); // parent toggles membership
  };

  const renderRow = (o, isChecked) => (
    <button
      aria-selected={isChecked}
      className={twMerge(
        "flex w-full cursor-pointer items-center gap-2 rounded-sm p-2.5 text-left text-sm",
        "hover:bg-gray100 focus:bg-gray100 focus:outline-none"
      )}
      data-active={isChecked ? "true" : undefined}
      key={o.value}
      onClick={() => handleToggle(o.value)}
      role="option"
      type="button"
    >
      <Checkbox isChecked={isChecked} />

      <span className="truncate">{o.text}</span>
    </button>
  );

  return (
    <div className={twMerge("relative", className)} ref={rootRef}>
      <Button
        className="flex w-full items-center gap-2 justify-between"
        color={values.length ? "primary50" : "gray100"}
        onClick={() => setOpen((o) => !o)}
        rounded="lg"
        size="sm"
        {...buttonProps}
      >
        <span className="truncate">{displayText}</span>
        <FiChevronDown
          className={twMerge(
            "shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="absolute z-20 mt-1 w-full"
            exit={{ opacity: 0, scale: 0.98, y: -4 }}
            initial={{ opacity: 0, scale: 0.98, y: -4 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            <div className="max-h-[28rem] overflow-hidden rounded-md border border-gray200 bg-white shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="text-base font-semibold text-black">
                  {header}
                </div>
                <Checkbox
                  isChecked={filtered?.selected?.length > 0}
                  isSome={
                    filtered?.selected?.length > 0 &&
                    filtered?.unselected?.length > 0
                  }
                  onChange={(value) => {
                    const allFiltered = [
                      ...filtered.selected,
                      ...filtered.unselected,
                    ];
                    if (value) {
                      // Select all filtered items
                      allFiltered.forEach((o) => {
                        if (!selectedSet.has(o.value)) {
                          onChange(o.value);
                        }
                      });
                    } else {
                      // Deselect all filtered items
                      allFiltered.forEach((o) => {
                        if (selectedSet.has(o.value)) {
                          onChange(o.value);
                        }
                      });
                    }
                  }}
                />
              </div>

              <Divider type="horizontal" color="gray200" />

              {/* Search */}
              <div className="px-4 py-3">
                <input
                  className={twMerge(
                    "h-9 w-full rounded-md border border-gray300 px-4 text-sm outline-none",
                    "focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
                  )}
                  name="search"
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search"
                  value={searchValue}
                />
              </div>

              <Divider type="horizontal" color="gray200" />

              {/* List */}
              <div
                className="max-h-80 overflow-y-auto"
                id={`${id}-listbox`}
                ref={listRef}
                role="listbox"
                tabIndex={-1}
              >
                <div className="px-1.5 py-1 flex flex-col gap-1">
                  {filtered.selected.map((o) => renderRow(o, true))}
                  {filtered.unselected.map((o) => renderRow(o, false))}
                  {!filtered.selected.length && !filtered.unselected.length && (
                    <div className="p-3 text-sm text-gray500">No results</div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
